import { resolveApiUrl } from '@/config/apiConfig'
import { useAppStore } from '@/store/appStore'

export type RuntimeMetricEvent = {
  type: 'fetch-aborted'
  path: string
  method: string
  at: number
}

type FetchAbortPayload = {
  input: string
  resolvedUrl: string
  method: string
}

const isBrowser = typeof window !== 'undefined'
const TELEMETRY_ENDPOINT = '/api/telemetry/runtime'
const MAX_BUFFER = 50
const MAX_QUEUE_AGE_MS = 5 * 60 * 1000
const pending: RuntimeMetricEvent[] = []
let flushTimer: number | null = null
let initialized = false
let initializedLogoutListener = false

const isTelemetryAllowed = () => {
  const state = useAppStore.getState()
  return state.isAuthenticated && state.telemetryConsent === 'granted'
}

const sanitizePath = (input: string) => {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const url = new URL(input, base)
    return url.pathname || '/'
  } catch {
    if (input.startsWith('/')) return input.split('?')[0] || '/'
    return '/' + input.split('?')[0]
  }
}

const enqueueMetric = (event: RuntimeMetricEvent) => {
  pending.push(event)
  if (pending.length > MAX_BUFFER) {
    pending.splice(0, pending.length - MAX_BUFFER)
  }
  scheduleFlush(500)
}

const scheduleFlush = (delay = 0) => {
  if (!isBrowser) return
  if (flushTimer !== null) return
  flushTimer = window.setTimeout(async () => {
    flushTimer = null
    await flush()
  }, delay)
}

const flush = async () => {
  if (!pending.length) return
  if (!isTelemetryAllowed()) {
    // Try again when state changes
    return
  }
  const now = Date.now()
  const batch = pending.splice(0, pending.length)
  const freshEvents = batch.filter((event) => now - event.at <= MAX_QUEUE_AGE_MS)
  if (!freshEvents.length) {
    return
  }
  try {
    const endpoint = resolveApiUrl(TELEMETRY_ENDPOINT)
    const state = useAppStore.getState()
    const token = state.authToken
    const body = JSON.stringify({ events: freshEvents })
    await fetch(endpoint, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    })
  } catch {
    // Re-queue the batch for a later attempt (drop oldest first to avoid duplicates)
    pending.unshift(...freshEvents)
    if (pending.length > MAX_BUFFER) {
      pending.splice(0, pending.length - MAX_BUFFER)
    }
    scheduleFlush(2000)
  }
}

const setupStoreSubscription = () => {
  const selector = (state: ReturnType<typeof useAppStore.getState>) => ({
    allowed: state.isAuthenticated && state.telemetryConsent === 'granted',
  })
  let previous = selector(useAppStore.getState())
  useAppStore.subscribe((state) => {
    const next = selector(state)
    if (!previous.allowed && next.allowed) {
      scheduleFlush()
    }
    if (previous.allowed && !next.allowed) {
      pending.length = 0
    }
    previous = next
  })
}

export const initializeRuntimeMetrics = () => {
  if (!isBrowser || initialized) return
  initialized = true
  setupStoreSubscription()
  if (!initializedLogoutListener) {
    initializedLogoutListener = true
    window.addEventListener('app:unauthorized', () => {
      pending.length = 0
    })
  }
  if (isTelemetryAllowed() && pending.length) {
    scheduleFlush()
  }
}

export const recordFetchAbort = ({ input, resolvedUrl, method }: FetchAbortPayload) => {
  if (!isBrowser) return
  const path = sanitizePath(resolvedUrl || input)
  enqueueMetric({
    type: 'fetch-aborted',
    path,
    method,
    at: Date.now(),
  })
}

export const forceFlushRuntimeMetrics = async () => {
  await flush()
}
